import { useState, useEffect, useRef } from 'react';
import { Download, FileCode2, FileText, FileSpreadsheet, FileArchive, FileImage, Trash2 } from 'lucide-react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/counter.css';
import { cn } from '../../lib/cn';
import {
  getPortalRoot,
  shouldDisableLightboxNoScroll,
} from '../../store/sidecarBehavior';
import type { MessageAttachment } from '../../services/messaging/messageAttachmentService';

interface Props {
  attachments: MessageAttachment[];
  isOwn: boolean;
  currentCollaboratorId?: string | null;
  canDeleteAny?: boolean;
  onDelete?: (att: MessageAttachment) => void;
  /** Appelé quand une image finit de charger — recale le scroll en bas. */
  onMediaLoad?: () => void;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

function iconFor(mime: string) {
  if (mime === 'text/html') return FileCode2;
  if (mime.startsWith('image/')) return FileImage;
  if (mime === 'application/pdf') return FileText;
  if (mime.includes('spreadsheet') || mime.includes('excel')) return FileSpreadsheet;
  if (mime.includes('zip')) return FileArchive;
  return FileText;
}

export function MessageAttachmentList({
  attachments,
  isOwn,
  currentCollaboratorId = null,
  canDeleteAny = false,
  onDelete,
  onMediaLoad,
}: Props) {
  const [lightboxIndex, setLightboxIndex] = useState<number>(-1);
  const rootRef = useRef<HTMLDivElement>(null);

  const canDeleteAtt = (att: MessageAttachment) =>
    !!onDelete && (canDeleteAny || att.uploaded_by === currentCollaboratorId);

  function handleDelete(att: MessageAttachment) {
    if (!onDelete) return;
    if (window.confirm(`Supprimer le fichier « ${att.filename} » ?`)) onDelete(att);
  }

  // Fermer la lightbox avec la flèche bas (en plus du drag/pull-down et d'Échap).
  useEffect(() => {
    if (lightboxIndex < 0) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setLightboxIndex(-1);
      }
    }
    const view = rootRef.current?.ownerDocument?.defaultView ?? window;
    view.addEventListener('keydown', onKey);
    return () => view.removeEventListener('keydown', onKey);
  }, [lightboxIndex]);

  if (attachments.length === 0) return null;

  const images = attachments.filter((a) => a.mime_type.startsWith('image/'));
  const others = attachments.filter((a) => !a.mime_type.startsWith('image/'));

  const slides = images
    .filter((att) => att.signed_url)
    .map((att) => ({ src: att.signed_url as string, alt: att.filename, title: att.filename }));

  return (
    <div
      ref={rootRef}
      className={cn('mt-1 flex flex-col gap-1.5', isOwn ? 'items-end' : 'items-start')}
    >
      {images.length > 0 && (
        <div className={cn('flex flex-wrap gap-1.5 max-w-xs', isOwn && 'justify-end')}>
          {images.map((att, i) => (
            <div key={att.id} className="relative group">
              <button
                type="button"
                onClick={() => setLightboxIndex(i)}
                className="block rounded-lg overflow-hidden border border-cime-border hover:opacity-90 transition-opacity cursor-zoom-in"
                title={att.filename}
              >
                <img
                  src={att.signed_url}
                  alt={att.filename}
                  className="max-h-40 max-w-[180px] object-cover"
                  loading="lazy"
                  onLoad={onMediaLoad}
                />
              </button>
              {canDeleteAtt(att) && (
                <button
                  type="button"
                  onClick={() => handleDelete(att)}
                  className="absolute top-1 right-1 p-1 rounded-md bg-black/50 text-white opacity-0 group-hover:opacity-100 hover:bg-red-600 transition"
                  title="Supprimer le fichier"
                  aria-label={`Supprimer ${att.filename}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <Lightbox
        open={lightboxIndex >= 0}
        index={lightboxIndex}
        close={() => setLightboxIndex(-1)}
        slides={slides}
        plugins={[Zoom, Counter]}
        carousel={{ finite: true }}
        controller={{ closeOnPullDown: true, closeOnBackdropClick: true }}
        counter={{ container: { style: { top: 'unset', bottom: 0 } } }}
        portal={{ root: () => getPortalRoot(rootRef.current) }}
        noScroll={{ disabled: shouldDisableLightboxNoScroll(rootRef.current) }}
      />

      {others.map((att) => {
        const Icon = iconFor(att.mime_type);
        return (
          <div
            key={att.id}
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg border text-xs max-w-xs transition-colors',
              isOwn
                ? 'bg-cime-primary/10 border-cime-primary/30 text-cime-text-primary'
                : 'bg-cime-surface border-cime-border text-cime-text-primary',
            )}
          >
            <a
              href={att.signed_url}
              target="_blank"
              rel="noopener noreferrer"
              download={att.filename}
              className="flex items-center gap-2 flex-1 min-w-0 hover:opacity-80 transition-opacity"
              title={att.filename}
            >
              <Icon className="w-4 h-4 flex-shrink-0 text-cime-text-muted" />
              <div className="flex-1 min-w-0">
                <div className="truncate font-medium">{att.filename}</div>
                <div className="text-[10px] text-cime-text-muted">{formatBytes(att.size_bytes)}</div>
              </div>
              <Download className="w-3.5 h-3.5 flex-shrink-0 text-cime-text-muted" />
            </a>
            {canDeleteAtt(att) && (
              <button
                type="button"
                onClick={() => handleDelete(att)}
                className="p-1 flex-shrink-0 rounded text-cime-text-muted hover:text-red-600 hover:bg-red-50 transition"
                title="Supprimer le fichier"
                aria-label={`Supprimer ${att.filename}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
